import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AbilityServiceSignal } from '@casl/angular';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { captureException } from '@sentry/angular';
import { AuthorizationModule } from '@wilson/authorization';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import {
  PublicationStatus,
  RoleAction,
  RolePermissionSubject,
  Shift,
  TimelineShift,
} from '@wilson/interfaces';
import { TimelineUpdateShiftV2 } from '@wilson/shift-timeline/state';
import { ShiftsService } from '@wilson/shifts';
import { cloneDeep } from 'lodash';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'wilson-release-multi-select-shift-button',
  imports: [
    NzButtonModule,
    TranslatePipe,
    FeatureFlagsModule,
    AuthorizationModule,
    NzTooltipModule,
  ],
  templateUrl: './release-multi-select-shift-button.component.html',
  styleUrl: './release-multi-select-shift-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReleaseMultiSelectShiftButtonComponent implements OnChanges {
  private readonly translate = inject(TranslateService);
  private readonly shiftsService = inject(ShiftsService);
  private readonly toaster = inject(NzMessageService);
  private readonly store = inject(Store);

  @Input({ required: true }) selectedShifts!: (Shift & { id: string })[];
  @Input() isOperationOnGoing = false;
  @Output() releaseShiftsSuccess = new EventEmitter();
  @Output() releaseShiftOperationOngoing = new EventEmitter();
  canReleaseShifts!: boolean;

  protected RoleAction = RoleAction;
  protected RolePermissionSubject = RolePermissionSubject;
  protected can = inject(AbilityServiceSignal).can;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedShifts']) {
      this.canReleaseShifts = this.selectedShifts.every(
        (shift) =>
          shift.userId &&
          (shift.publicationStatus === PublicationStatus.NotPublished ||
            shift.publicationStatus === PublicationStatus.NotPublishedAgain),
      );
    }
  }

  protected async releaseShifts(): Promise<void> {
    this.releaseShiftOperationOngoing.emit(true);
    try {
      await firstValueFrom(
        this.shiftsService.bulkPublishShifts(this.selectedShifts),
      );
      this.updateTimelineShifts();
      this.releaseShiftsSuccess.emit();
      this.releaseShiftOperationOngoing.emit(false);
    } catch (error) {
      console.error(error);
      captureException(error);
      this.toaster.error(
        this.translate.instant('general.something_went_wrong'),
      );
      this.releaseShiftOperationOngoing.emit(false);
    }
  }

  private updateTimelineShifts(): void {
    const clonedSelectedShifts = cloneDeep(this.selectedShifts);
    clonedSelectedShifts.forEach((shift) => {
      shift.publicationStatus = PublicationStatus.Published;
      this.store.dispatch(
        new TimelineUpdateShiftV2({
          ...shift,
          isHighlighted: true,
        } as TimelineShift),
      );
    });

    this.toaster.success(
      this.translate.instant('shift.page.success_multiple_release'),
    );
  }
}
